import { effectiveQuickDurationMin, type LiveProject, type ProjectsById } from '@wiztime/core';
import { useRef, type CSSProperties, type MouseEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../hooks/useSettings';
import { cx } from '../../../lib/cx';
import { PointerKind } from '../../../lib/input';
import { projectHex } from '../../../lib/projectColor';
import { categoryTagOf } from '../../../lib/projectLabel';
import { useDayUi } from '../dayUiStore';
import { tileBesideClick, type BesideMarkRecord, type CreateParallel } from '../tileMark';
import {
  QUICK_MARK_LONGPRESS_MS,
  QUICK_MARK_MOVE_CANCEL_PX,
  QuickMarkMode,
  QuickMarkSource,
} from '../quickMark';
import { FloatTip } from '../timeline/FloatTip';
import { DropTarget, useTileDrag } from '../timeline/useTileDrag';
import { useDayCommands } from '../useDayCommands';
import { TileDragCard } from './TileDragCard';
import styles from './Cards.module.css';

/** Settings and the write the tiles of one card need to be dragged onto the timeline (2026-10c §2). */
export interface TileDragContext {
  readonly settings: ReturnType<typeof useSettings>;
  readonly createBlock: CreateParallel;
}

/** Shared by the tiles of one card. */
export function useTileDragContext(): TileDragContext {
  const settings = useSettings();
  const { createBlock } = useDayCommands();
  return { settings, createBlock };
}

/** Tracks one tile's touch long press (2026-10b §4.2) and the click that trails it. */
interface LongPress {
  timer: number | null;
  startX: number;
  startY: number;
  /** The long press locked already: the trailing click must not act (it would exit at once). */
  suppressClick: boolean;
  /** Last pointer type seen: a touch `contextmenu` is the long-press menu and is suppressed. */
  touch: boolean;
  /** The last click consumed by the parallel add: the second click of its double click is swallowed. */
  besideMark: BesideMarkRecord | null;
}

/** Small padlock for the "locked" pill (§4.4, §4.5); text colour, 12px. */
export function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <rect x="2" y="5" width="8" height="6" rx="1.2" fill="currentColor" />
      <path d="M4 5V3.6a2 2 0 0 1 4 0V5" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

/**
 * One project tile (§4.2 events, §4.5 variant A selection): a single click arms one mark
 * (Once), a double click or touch long press locks continuous marking (Locked). The compact
 * face of the week sidebar is shorter, drops the category tag and shrinks the state pill to its icon.
 */
export function QuickMarkTile({
  project,
  mode,
  ordinal,
  projectsById,
  drag,
  compact = false,
  child = false,
  onMenu,
  onRename,
}: {
  project: LiveProject;
  mode: QuickMarkMode;
  /** 1-based ordinal shown on the tile; the digit keys address the first nine (§4.8). Child projects have none. */
  ordinal?: number;
  /** Every project, to resolve a child project's inherited colour. */
  projectsById: ProjectsById;
  /** What dragging the tile onto the timeline needs (2026-10c §2). */
  drag: TileDragContext;
  compact?: boolean;
  /** A child project's tile: about 60% of a top-level tile's height, no ordinal or category tag. */
  child?: boolean;
  /** Mouse right click, menu key or Shift+F10: open the tile menu at the point (design 2026-10-15 §2). */
  onMenu: (project: LiveProject, anchor: HTMLElement, point: { readonly x: number; readonly y: number }) => void;
  /** F2: rename in place. */
  onRename: (project: LiveProject, anchor: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const tileDrag = useTileDrag({
    projectId: project.id,
    durationMin: effectiveQuickDurationMin(project.data, drag.settings),
    createBlock: drag.createBlock,
  });
  const press = useRef<LongPress>({
    timer: null,
    startX: 0,
    startY: 0,
    suppressClick: false,
    touch: false,
    besideMark: null,
  });
  const on = mode !== QuickMarkMode.Off;
  const category = compact || ordinal === undefined ? null : categoryTagOf(project.data.name, t(`category.${project.data.category}`));
  const name =
    mode === QuickMarkMode.Once
      ? t('quickMark.tileOnce', { project: project.data.name })
      : mode === QuickMarkMode.Locked
        ? t('quickMark.tileLocked', { project: project.data.name })
        : project.data.name;

  const cancelTimer = (): void => {
    if (press.current.timer !== null) window.clearTimeout(press.current.timer);
    press.current.timer = null;
  };

  const onPointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    tileDrag.handlers.onPointerDown(event);
    press.current.touch = event.pointerType === PointerKind.Touch;
    press.current.suppressClick = false;
    if (event.pointerType !== PointerKind.Touch) return;
    cancelTimer();
    press.current.startX = event.clientX;
    press.current.startY = event.clientY;
    // Holding a tile 380 ms locks (§4.2); the trailing click is suppressed, or it would be
    // read as "click the locked tile" and exit at once.
    press.current.timer = window.setTimeout(() => {
      press.current.timer = null;
      press.current.suppressClick = true;
      useDayUi.getState().quickMarkLock(project.id, true);
      try {
        navigator.vibrate?.(10);
      } catch {
        // Vibration is a progressive enhancement only.
      }
    }, QUICK_MARK_LONGPRESS_MS);
  };

  const onPointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
    tileDrag.handlers.onPointerMove(event);
    if (press.current.timer === null) return;
    // The tile may sit in a scrollable container: movement past 10px is a scroll, not a press.
    const distance = Math.hypot(event.clientX - press.current.startX, event.clientY - press.current.startY);
    if (distance > QUICK_MARK_MOVE_CANCEL_PX) cancelTimer();
  };

  const onClick = (event: MouseEvent<HTMLButtonElement>): void => {
    // The click that trails a completed drag must not arm the brush (2026-10c §2).
    if (tileDrag.consumeClick()) return;
    if (press.current.suppressClick) {
      press.current.suppressClick = false;
      return;
    }
    const ui = useDayUi.getState();
    // A keyboard activation (Enter/Space) arrives as a click with detail 0: it acts like a
    // single click without the double-click window; with Shift it locks (§4.2).
    if (event.detail === 0 && event.shiftKey) {
      ui.quickMarkLock(project.id);
      return;
    }
    // With a block group selected the tile adds a parallel block beside it and does not arm
    // quick mark (design 2026-10-15 §1.2).
    const beside = tileBesideClick(press.current.besideMark, project.id, event.detail, drag.createBlock, t);
    press.current.besideMark = beside.record;
    if (beside.consumed) return;
    if (event.detail === 0) {
      ui.quickMarkActivate(project.id, QuickMarkSource.Key);
      return;
    }
    ui.quickMarkActivate(project.id, QuickMarkSource.Pointer, event.detail);
  };

  return (
    <button
      type="button"
      className={cx(
        styles.tile,
        compact && styles.compactTile,
        child && styles.childTile,
        mode === QuickMarkMode.Once && styles.tileOnce,
        mode === QuickMarkMode.Locked && styles.tileLocked,
        tileDrag.dragging && styles.tileDragging,
      )}
      style={{ '--tile-color': projectHex(project, projectsById) } as CSSProperties}
      aria-pressed={on}
      aria-label={name}
      title={project.data.name}
      data-testid="quick-mark-project"
      data-mode={mode}
      data-dragging={tileDrag.dragging ? '' : undefined}
      data-drop-target={tileDrag.dragging ? tileDrag.dropTarget : undefined}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => {
        cancelTimer();
        tileDrag.handlers.onPointerUp(event);
      }}
      onPointerCancel={(event) => {
        cancelTimer();
        tileDrag.handlers.onPointerCancel(event);
      }}
      onLostPointerCapture={tileDrag.handlers.onLostPointerCapture}
      onPointerLeave={cancelTimer}
      onContextMenu={(event) => {
        event.preventDefault();
        // The touch long press means "lock", never the context menu (§4.2).
        if (press.current.touch) return;
        const rect = event.currentTarget.getBoundingClientRect();
        // The menu key reports no pointer position: hang the menu from the tile's corner instead.
        const fromKeyboard = event.clientX === 0 && event.clientY === 0;
        onMenu(project, event.currentTarget, {
          x: fromKeyboard ? rect.left + 12 : event.clientX,
          y: fromKeyboard ? rect.top + rect.height / 2 : event.clientY,
        });
      }}
      onKeyDown={(event) => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const menuKey = event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey);
        if (!menuKey && event.key !== 'F2') return;
        // Consumed here: the day view's own F2 / menu key act on blocks.
        event.preventDefault();
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        if (menuKey) onMenu(project, event.currentTarget, { x: rect.left + 12, y: rect.top + rect.height / 2 });
        else onRename(project, event.currentTarget);
      }}
    >
      <span className={styles.tileBar} aria-hidden="true" />
      {ordinal === undefined ? null : (
        <span className={styles.tileOrdinal} aria-hidden="true">
          {ordinal}
        </span>
      )}
      <span className={styles.tileName}>{project.data.name}</span>
      {category === null ? null : <span className={styles.tileCategory}>{category}</span>}
      {on ? (
        <span className={cx(styles.tilePill, compact && styles.compactPill)} aria-hidden="true">
          {mode === QuickMarkMode.Once ? '✓' : <LockIcon />}
          {compact ? null : mode === QuickMarkMode.Once ? t('quickMark.once') : t('quickMark.locked')}
        </span>
      ) : null}
      {tileDrag.dragging ? (
        <TileDragCard project={project} projectsById={projectsById} pointRef={tileDrag.pointRef} allowed={tileDrag.dropTarget === DropTarget.Lane} />
      ) : null}
      {tileDrag.tipSpan !== null ? <FloatTip span={tileDrag.tipSpan} pointRef={tileDrag.pointRef} /> : null}
    </button>
  );
}
